import camelcase from 'camelcase';
import { ValidationResult, DataGetter } from '../types/data';
import { SortDirection } from '../types/ux';

type AggregateFunc = (prevValue: number, record: Record<string, unknown>) => number;
type FilterFunc = (record: Record<string, unknown>) => boolean;

export function sum(keyMember: string): AggregateFunc {
  return (
    value: number,
    record: Record<string, unknown>,
  ): number => value + Number(record[keyMember]);
}

export function count(): AggregateFunc {
  return (
    value: number,
  ): number => value + 1;
}

export function aggregate(
  records: Record<string, unknown>[],
  aggrFunc: AggregateFunc,
  groupByKeys?: string[],
  filterFunc?: FilterFunc,
): Record<string, unknown> | unknown {
  if (!groupByKeys || groupByKeys.length === 0) {
    return records.reduce(
      (result, record) => aggrFunc(result, record),
      0,
    );
  }

  const groupedResult: Record<string, unknown> = {};
  records
    .filter((record) => (filterFunc ? filterFunc(record) : true))
    .forEach((record) => groupByKeys
      .reduce(
        (grouped, keyMember, idx) => {
          const modifiedGrouped = grouped;
          const key = String(record[keyMember]);

          // If not the last groupKey, create nested Record and return the nest for next iteration.
          if (idx < groupByKeys.length - 1) {
            if (!modifiedGrouped[key]) {
              modifiedGrouped[key] = {};
            }
            return modifiedGrouped[key] as Record<string, unknown>;
          }

          // If this is the last groupKey, apply aggregation with default as 0.
          if (!modifiedGrouped[key]) {
            modifiedGrouped[key] = 0;
          }

          const prevValue = modifiedGrouped[key] as number;
          modifiedGrouped[key] = aggrFunc(prevValue, record);

          return modifiedGrouped;
        },
        groupedResult,
      ));

  return groupedResult;
}

export function refineValidationResult(validationResult?: ValidationResult) {
  if (!validationResult) {
    return undefined;
  }

  return {
    ...validationResult,
    exceptions: validationResult.exceptions && validationResult.exceptions.map((exception) => ({
      ...exception,
      dataPath: exception.dataPath && camelcase(exception.dataPath),
    })),
  } as ValidationResult;
}

function normalizeSortableData(
  value?: unknown,
  sortDirection?: SortDirection,
  ignoreEmpty?: boolean,
) {
  if (!ignoreEmpty || !sortDirection || value) {
    return value;
  }

  return sortDirection === 'ASC' ? Infinity : -Infinity;
}

export function applySort<T extends Record<string, unknown>>(
  data: T[],
  sortDataKey?: string,
  sortDirection?: SortDirection,
  sortDataGetter?: DataGetter,
  options?: { ignoreEmpty?: boolean },
) {
  if (!sortDataKey || !sortDirection) {
    return data;
  }

  const revert = sortDirection === 'ASC' ? 1 : -1;
  const toSort = [...data];

  toSort.sort((a: T, b: T) => {
    const valueA = sortDataGetter ? sortDataGetter(a) : a[sortDataKey];
    const valueB = sortDataGetter ? sortDataGetter(b) : b[sortDataKey];

    const normalizedA = normalizeSortableData(valueA, sortDirection, options?.ignoreEmpty) as any;
    const normalizedB = normalizeSortableData(valueB, sortDirection, options?.ignoreEmpty) as any;

    if (normalizedA < normalizedB || normalizedA === -Infinity || normalizedB === Infinity) {
      return -1 * revert;
    }

    if (normalizedA > normalizedB || normalizedA === Infinity || normalizedB === -Infinity) {
      return 1 * revert;
    }

    return 0;
  });

  return toSort;
}

export function lbsToKgs(lbs: number) {
  return Math.round(lbs * 0.45359237);
}

export function kgsToLbs(kgs: number) {
  return Math.round(kgs / 0.45359237);
}

export function isNullOrUndefined(data?: unknown) {
  return (data === null || data === undefined);
}

export function isUuid(input: string) {
  // Our UUID generated by backend is not actually valid UUID...
  // So we use this pattern.
  const regex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
  return !!regex.test(input);
}

export function chunk(input: Array<unknown>, size: number) {
  return Array.from({ length: Math.ceil(input.length / size) }, (_, i) => (
    input.slice(i * size, i * size + size)
  ));
}
